import { Injector }             from '@angular/core';
import {
    CurrencyNames, IOrderInfo,
    ISymbol, ITicker, Market,
    OrderState }                from './market-base.class';
import { IMarketSettingsToken } from './IMarketSettingsToken';
import { TranslateService }     from '@ngx-translate/core';
import CryptoJS                 from 'crypto-js';

type KrakenResponse = {
    error:      Array<any>;
    result?:    {[key: string]: any};
}

export class MarketKraken extends Market<IMarketSettingsToken>
{
    private currenciesName: {[name: string]: CurrencyNames } = {
        JASMY1: 'JASMY',
        XETH:   'ETH',
        ZEUR:   'EUR',
        ZUSD:   'USD',
        XXBT:   'XBT',
        XLTC:   'LTC',
        ZAUD:   'AUD',
        ZCAD:   'CAD',
        ZGBP:   'GBP',
        ZJPY:   'JPY',
        XXRP:   'XRP'
    };
    
    private translateService: TranslateService;

    protected name:              string      = 'Kraken';
    protected icon:              string      = '/assets/images/exchanges/kraken.svg';

    private accessUrl:           string      = 'https://api.kraken.com';
    private currencies:          string[]    = [];
    private symbols:             ISymbol[]   = [];
    private prefferedCurrencies: string[]    = ['USDT', 'JASMY1', 'XETH'];
    private balances:            Object      = {};
    private forceBalances:       boolean     = false;
    private proxyApi:            string      = '/api/market/kraken';
    
    constructor(
        protected injector: Injector,
        protected marketSettings: IMarketSettingsToken        
    ) {
        super(injector, marketSettings);
        this.translateService = this.injector.get(TranslateService);
    }

    //#region Market abstract implementations

    public async checkConnection(): Promise<boolean>
    {
        await this.getBalance('ETH');

        return true;
    }

    public async getCurrencies(): Promise<string[]>
    {
        if (this.currencies.length == 0){
            await this.readAssetPairs();
        }

        const displayCurrencies = this.currencies.map((c) => this.getCurrencyDisplay(c));
        return displayCurrencies;
    }

    public async getSymbols(): Promise<ISymbol[]>
    {
        if (this.symbols.length == 0){
            await this.readAssetPairs();
        }

        return this.symbols;
    }

    public async getBalance(_currency: string): Promise<string>
    {
        const currency = _currency.toUpperCase();

        if (!!this.balances[currency] && !this.forceBalances) return this.balances[currency];

        const endpoint = '/0/private/Balance';
        const url      = `${this.accessUrl}${endpoint}`;

        const params = {
            nonce: (Date.now() * 1000).toString()
        };

        const queryString = this.createQueryString(params);

        const headers   = this.getHeaders(endpoint, params);

        const response = <KrakenResponse>await this.http.post(this.proxyApi, {
                                                            url,
                                                            method: 'POST',
                                                            body: queryString,
                                                            headers
                                                         })
                                                         .toPromise();

        if (response.error.length !== 0){
            throw new Error(response.error.join(' < '));
        }

        if(response.result) {
            for (const b in response.result) {
                this.balances[this.getCurrencyDisplay(b)] = response.result[b];
            }    
        }

        if(!this.balances[currency]) this.balances[currency] = '0.00';
        return this.balances[currency];
    }

    public async getTicker(_symbol: string): Promise<ITicker>
    {
        const endpoint = `/0/public/Ticker?pair=${_symbol.toUpperCase()}`;

        const url = `${this.accessUrl}${endpoint}`;

        const response = <KrakenResponse>await this.http.post(this.proxyApi, {
            url,
            method: 'GET',
        })
        .toPromise();

        if (response.error.length !== 0){
            throw new Error(response.error.join(' < '));
        }

        return {
            buy:  response.result[_symbol.toUpperCase()].a[0],
            sell: response.result[_symbol.toUpperCase()].b[0]
        };
    }

    public getOrderInfoUrl(_orderId: string, _symbol?: string): {url: string, data: any}
    {
        const endpoint = '/0/private/QueryOrders';
        const url      = `${this.accessUrl}${endpoint}`;

        const params = {
            nonce: (Date.now() * 1000).toString(),
            txid: _orderId
        };

        const queryString = this.createQueryString(params);

        const headers   = this.getHeaders(endpoint, params);

        return {
            url:  url,
            data: {
                      body: queryString,
                      headers
                  }
        }
    }

    public async getOrderInfo(_orderId: string, _symbol: string): Promise<IOrderInfo>
    {
        const endpoint = '/0/private/QueryOrders';
        const url      = `${this.accessUrl}${endpoint}`;

        const params = {
            nonce: (Date.now() * 1000).toString(),
            txid: _orderId
        };

        const queryString = this.createQueryString(params);

        const headers   = this.getHeaders(endpoint, params);

        const response = <KrakenResponse>await this.http.post(this.proxyApi, {
                                                                                  url,
                                                                                  method: 'POST',
                                                                                  body: queryString,
                                                                                  headers
                                                                             })
                                                                             .toPromise();

        if (response.error.length !== 0){
            throw new Error(response.error.join(' < '));
        }

        const orderData = response.result[_orderId];
        if (!orderData)
        {
            throw new Error('ORDER_NOT_FOUND');
        }

        return this.parseOrderInfo(orderData);
    }

    public async getOrdersHistory(): Promise<IOrderInfo[]>
    {
        const endpoint = '/0/private/TradesHistory';
        const url      = `${this.accessUrl}${endpoint}`;

        const params = {
            nonce: (Date.now() * 1000).toString()
        };

        const queryString = this.createQueryString(params);

        const headers   = this.getHeaders(endpoint, params);

        const response = <KrakenResponse>await this.http.post(this.proxyApi, {
                                                                                  url,
                                                                                  method: 'POST',
                                                                                  body: queryString,
                                                                                  headers
                                                                             })
                                                                             .toPromise();

        if (response.error.length !== 0){
            throw new Error(response.error.join(' < '));
        }

        const orderData: any = Object.values(response.result['trades']);

        if (!orderData || response.result['count'] === 0) return [];

        console.log(orderData)

        return orderData.map(_order => this.parseOrderInfo(_order));
    }

    public async createOrder(
        _symbol: string,
        _amount: string,
        _type: 'buy' | 'sell'
    ): Promise<string>
    {
        const symbol = this.symbols.find(s => s.symbol == _symbol);

        const amount = _type != 'buy' ? +_amount : await this.convertAmount(+_amount, symbol, _type);

        const orderMin = symbol['order_min'];
        if(orderMin && Number(amount) < Number(orderMin)){
            const errorMessage: string = this.translateService.instant(
                'FORM.AMOUNT.ERRORS.MIN-ORDER-LIMIT',
                { min_amount: orderMin, symbol: symbol.base_currency }
            );
            throw new Error(errorMessage);
        }

        const endpoint = '/0/private/AddOrder';
        const url      = `${this.accessUrl}${endpoint}`;

        const params = {
            nonce: (Date.now() * 1000).toString(),
            pair:       _symbol,
            type:       _type,
            ordertype:  'market',
            volume:     amount
        };

        const queryString = this.createQueryString(params);

        const headers   = this.getHeaders(endpoint, params);

        const response = <KrakenResponse>await this.http.post(this.proxyApi, {
                                                                                  url,
                                                                                  method: 'POST',
                                                                                  body: queryString,
                                                                                  headers
                                                                             })
                                                                             .toPromise();

        if (response.error.length !== 0){
            throw new Error(response.error.join(' < '));
        }

        this.balances = {};
        return response.result.txid[0];
    }
    //#endregion

    public getCurrencyDisplay(_currency: string): CurrencyNames {
        const currencyDisplay = this.currenciesName[_currency] || super.getCurrencyDisplay(_currency);
        return currencyDisplay;
    }

    //#region private methods

    private async readAssetPairs(){

        const endpoint = '/0/public/AssetPairs';
        const url      = `${this.accessUrl}${endpoint}`;

        const response = <KrakenResponse>await this.http.post(this.proxyApi, {
            url,
            method: 'GET',
        })
        .toPromise();

        if (response.error.length !== 0){
            throw new Error(response.error.join(' < '));
        }

        const coinsBase  = new Set(Object.values(response.result).map(_asset => _asset.base));
        const coinsQuote = new Set(Object.values(response.result).map(_asset => _asset.quote));

        const coins = new Set([...coinsBase, ...coinsQuote]);
        this.currencies = this.sortCurrencies(Array.from(coins));

        this.symbols = Object.values(response.result).map(_asset =>
            {
                return {
                            orig_symbol:    `${_asset.base}${_asset.quote}`,
                            order_min:      _asset.ordermin,
                            base_currency:  this.getCurrencyDisplay(_asset.base),
                            quote_currency: this.getCurrencyDisplay(_asset.quote),
                            symbol:         _asset.altname,
                            precision:      Math.max(_asset.lot_decimals, _asset.pair_decimals).toString()
                        }
            });
    }

    private parseOrderInfo(_orderInfo: any): IOrderInfo
    {
        const symbol = _orderInfo.pair || _orderInfo.descr.pair;
        const type = _orderInfo.type || _orderInfo.descr.type;

        const currencies = this.coinsService.parseExchangeSymbol(symbol);

        const currencySend = type === 'buy' ? currencies[1] : currencies[0];
        const currencyGet = type === 'buy' ? currencies[0] : currencies[1];

        const sendAmount = type === 'buy' ? _orderInfo.cost : (_orderInfo.vol_exec || _orderInfo.vol);
        const getAmount  = type === 'buy' ? (_orderInfo.vol_exec || _orderInfo.vol) : _orderInfo.cost;

        const state = this.parseOrderState(_orderInfo['status'], _orderInfo['misc']);
        const time = Math.round((_orderInfo.time || _orderInfo.closetm || _orderInfo.opentm || _orderInfo.starttm) * 1000);

        let orderInfo = {
            orderId:        _orderInfo['ordertxid'],
            currencySend,
            sendAmount,
            currencyGet,
            getAmount,
            fee:            _orderInfo.fee,
            finishedAt:     time.toString(),
            price:          _orderInfo.price,
            state,
            type,
            symbol
        };

        orderInfo['postxid'] = _orderInfo['postxid'];

        return <IOrderInfo>orderInfo;
    }

    private parseOrderState(_status, _misc): OrderState {
        if(!_status) return 'complete';

        switch(_status){
            case 'closed':      return 'complete';
            case 'canceled':    return _misc == 'partial' ? 'complete' : 'canceled';
            default:            return 'processing';
        }        
    }

    private createQueryString(_params: Object = {})
    {
        const ordered = Object.keys(_params).sort();

        let result = '';

        ordered.forEach((_key) => {
            result += `&${_key}=${_params[_key]}`;
        });

        return result.slice(1);
    }

    private getQuerySignature(_endpoint: string, _params: {[key: string]: string | number})
    {
        const secret = CryptoJS.enc.Base64.parse(this.settings.secret_key);
        const request = this.createQueryString(_params);
        const hashDigest = CryptoJS.SHA256(_params.nonce + request);

        const hmac = CryptoJS.algo.HMAC.create(CryptoJS.algo.SHA512, secret);
        hmac.update(_endpoint);
        hmac.update(hashDigest);

        return CryptoJS.enc.Base64.stringify(hmac.finalize());
    }

    private getHeaders(_endpoint: string, _params: any): Object
    {
        return {
            'API-Key' : this.settings.access_key,
            'API-Sign' : this.getQuerySignature(_endpoint, _params)
        };
    }

    private sortCurrencies(_currencies: string[]): string[]
    {
        return _currencies.sort((a, b) =>
        {
            const indexA = this.prefferedCurrencies.indexOf(a);
            const indexB = this.prefferedCurrencies.indexOf(b);
            if (indexA === -1) return 0;

            if (indexA < indexB) return -1;

            if (indexA !== -1 && indexB === -1) return -1;

            if (indexA > indexB) return 1;

            return 0;
        });
    }

    //#endregion
}
